import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import tokens from '../../theme/tokens'; // Ensure correct path to tokens

/**
 * A reusable header component for collapsible sections/categories.
 *
 * @param {object} props - Component props.
 * @param {string} props.title - The text to display as the header title.
 * @param {boolean} props.isOpen - Whether the section is currently open/expanded.
 * @param {function} props.toggleOpen - Callback function to toggle the open state.
 * @param {boolean} [props.isCategory=false] - Optional flag to apply category-specific styling (overline, smaller margin).
 * @param {object} [props.sx] - Optional additional sx props to apply to the root Box.
 */
const CollapsibleSectionHeaderComponent = ({ title, isOpen, toggleOpen, isCategory = false, sx = {} }) => {
  return (
    <Box
      onClick={toggleOpen}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: isCategory ? tokens.spacing[2] : tokens.spacing[3], // Different margin based on type
        cursor: 'pointer',
        borderBottom: isCategory ? `1px solid ${tokens.colors.borders.light}` : 'none', // Border only for categories
        pb: isCategory ? tokens.spacing[1] : 0,
        ...sx, // Allow overriding styles
      }}
    >
      <Typography
        // Use overline for categories, default variant otherwise (can be overridden by parent)
        variant={isCategory ? "overline" : undefined}
        display="block"
        sx={{
          fontWeight: tokens.typography.fontWeights.medium,
          // Adjust font size and color based on type
          fontSize: isCategory ? tokens.typography.fontSizes.xs : tokens.typography.fontSizes.lg,
          color: isCategory ? tokens.colors.text.secondary : tokens.colors.text.primary,
          lineHeight: tokens.typography.lineHeights.tight,
          textTransform: isCategory ? 'uppercase' : 'none', // Uppercase only for categories
          flexGrow: 1,
        }}
      >
        {title}
      </Typography>
      <IconButton
        size="small"
        aria-label={isOpen ? 'collapse section' : 'expand section'}
        sx={{ color: tokens.colors.text.secondary, p: 0 }}
      >
        <ExpandMoreIcon sx={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: tokens.transitions.standard }} />
      </IconButton>
    </Box>
  );
}

const CollapsibleSectionHeader = React.memo(CollapsibleSectionHeaderComponent);

export default CollapsibleSectionHeader; 